import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createContextualCan } from '@casl/react'
import { apiGet } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import type { AbilityPayload } from '@/types/entities'
import { createAppAbility, emptyAbility, type AppAbility } from './ability'

const AbilityContext = createContext<AppAbility>(emptyAbility())

/**
 * The caller's identity in DOMAIN terms. Firebase's auth.currentUser.uid is NOT the member
 * id — a member can exist before their uid does (invited by email) — so anything comparing
 * against assigneeIds or ownerUserId must use this.
 */
interface Identity {
  userId: string
  email: string
  /** How the caller appears to everyone else, and to themselves in the top bar. */
  displayName: string
  photoUrl: string | null
  /** Built-in/custom role reference from the backend; used only for exact role-gated UI. */
  roleRef: string
  roleLabel: string
}

const IdentityContext = createContext<Identity>({
  userId: '',
  email: '',
  displayName: '',
  photoUrl: null,
  roleRef: '',
  roleLabel: '',
})

/**
 * The backend's own list of GRANTED permission keys, verbatim from `GET
 * /permissions/me/abilities`.
 *
 * `can()` remains the way to ask every question it can answer. This exists for the handful of
 * permissions that collapse onto the same CASL (action, subject) pair and are therefore
 * indistinguishable in the packed ruleset — `view.share` and `view.manageAny` are both
 * `manage SavedView`, so `can('manage', asView(v))` cannot tell "may publish my own view"
 * from "may edit yours". Reading the backend's answer re-implements nothing; guessing from
 * a role would.
 */
const PermissionKeysContext = createContext<ReadonlySet<string>>(new Set())

/** <Can I="update" a="Card" this={card}>…</Can> */
export const Can = createContextualCan(AbilityContext.Consumer)

interface AbilityProviderProps {
  workspaceId?: string
  children: ReactNode
  /** Rendered while the ruleset loads. */
  fallback?: ReactNode
  onError?: (error: unknown) => ReactNode
}

export function AbilityProvider({
  workspaceId,
  children,
  fallback = null,
  onError,
}: AbilityProviderProps) {
  const { data, isPending, error } = useQuery({
    queryKey: qk.abilities(workspaceId),
    queryFn: () =>
      apiGet<AbilityPayload>('permissions/me/abilities', workspaceId ? { workspaceId } : undefined),
    staleTime: 5 * 60_000,
  })

  const ability = useMemo(
    () => (data ? createAppAbility(data.rules) : emptyAbility()),
    [data],
  )
  const granted = useMemo(() => new Set(data?.permissions ?? []), [data])

  // Never render children with a permissive default while loading — a flash of content the
  // user cannot actually act on is worse than a moment of skeleton.
  if (isPending) return <>{fallback}</>
  if (error) return <>{onError?.(error) ?? null}</>

  return (
    <IdentityContext.Provider
      value={{
        userId: data.userId,
        email: data.email,
        displayName: data.displayName,
        photoUrl: data.photoUrl,
        roleRef: data.role.ref,
        roleLabel: data.role.label,
      }}
    >
      <PermissionKeysContext.Provider value={granted}>
        <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
      </PermissionKeysContext.Provider>
    </IdentityContext.Provider>
  )
}

export function useAbility(): AppAbility {
  return useContext(AbilityContext)
}

/** The signed-in member's domain identity. Prefer this over auth.currentUser.uid. */
export function useIdentity(): Identity {
  return useContext(IdentityContext)
}

/** The granted permission keys. Read it through `usePermission().has()`, not directly. */
export function useGrantedPermissions(): ReadonlySet<string> {
  return useContext(PermissionKeysContext)
}

export { AbilityContext }
